<template>
  <div class="sort-filter-dropdown">
    <themed-button 
      @click="toggleDropdown"
      variant="outline"
      size="icon"
      aria-haspopup="true"
      :aria-expanded="isOpen"
      aria-label="Sort and filter options"
      :disabled="disabled"
      class="sort-filter-dropdown__button"
    >
      <theme-icon icon="filter-icon" type="icons" classes="flex w-4 h-4" />
      
      <!-- Active filters badge -->
      <span 
        v-if="activeFiltersCount > 0" 
        class="sort-filter-dropdown__badge"
        :aria-label="`${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''} active`"
      >
        {{ activeFiltersCount }}
      </span>
    </themed-button>
    
    <div v-if="isOpen" class="themed-dropdown__panel" role="menu">
      <!-- Sort Section -->
      <div v-if="sortOptions && sortOptions.length > 0" class="themed-dropdown__section">
        <h3 class="themed-dropdown__section-title">Sort by</h3>
        <div class="sort-filter-dropdown__sort-row">
          <themed-select 
            :value="sortBy" 
            :options="sortOptions"
            @input="handleSortChange"
            placeholder="None"
          />
          
          <themed-button 
            v-if="sortBy"
            @click="toggleSortDirection"
            variant="outline"
            size="icon"
            :title="sortDirection === 'asc' ? 'Ascending' : 'Descending'"
          >
            <theme-icon 
              icon="arrows-vertical" 
              type="icons" 
              classes="w-4 h-4"
              :class="sortDirection === 'desc' ? 'rotate-180' : ''"
            />
          </themed-button>
        </div>
      </div>
      
      <!-- Filter Section -->
      <div v-if="filterOptions && filterOptions.length > 0" class="themed-dropdown__section">
        <h3 class="themed-dropdown__section-title">Filters</h3>
        <div v-for="filter in filterOptions" :key="filter.key || filter.model || filter.label" class="themed-dropdown__filter-item">
          <!-- Toggle Filter -->
          <themed-toggle 
            v-if="filter.type === 'toggle'"
            :value="filter.value"
            :label="filter.label"
            @input="handleFilterChange(filter, $event)"
          />
          
          <!-- Select Filter -->
          <div v-else-if="filter.type === 'select'" class="themed-dropdown__select-filter">
            <label v-if="filter.label" class="themed-dropdown__filter-label">
              {{ filter.label }}
            </label>
            <themed-select
              :value="filter.value"
              :options="filter.options"
              :placeholder="filter.placeholder || 'Select...'"
              @input="handleFilterChange(filter, $event)"
            />
          </div>
          
          <!-- Date Range Filter -->
          <div v-else-if="filter.type === 'date-range'" class="themed-dropdown__date-range-filter">
            <label v-if="filter.label" class="themed-dropdown__filter-label">
              {{ filter.label }}
            </label>
            <div class="themed-dropdown__date-range-inputs">
              <themed-date-picker
                :value="filter.startValue"
                :placeholder="filter.startPlaceholder || 'Start Date'"
                format="MM/DD/YYYY"
                @input="handleFilterChange({ model: filter.startModel }, $event)"
              />
              <span class="themed-dropdown__date-range-separator">to</span>
              <themed-date-picker
                :value="filter.endValue"
                :placeholder="filter.endPlaceholder || 'End Date'"
                format="MM/DD/YYYY"
                @input="handleFilterChange({ model: filter.endModel }, $event)"
              />
            </div>
          </div>
        </div>
      </div>
      
      <!-- Actions -->
      <div class="themed-dropdown__actions">
        <themed-button 
          @click="handleClearFilters"
          variant="outline"
          size="sm"
        >
          Clear
        </themed-button>
        <themed-button 
          @click="isOpen = false"
          variant="default"
          size="sm"
        >
          Close
        </themed-button>
      </div>
    </div>
    
    <!-- Backdrop -->
    <div
      v-if="isOpen"
      class="sort-filter-dropdown__backdrop"
      @click="isOpen = false"
    ></div>
  </div>
</template>

<script>
import ThemedButton from '../ThemedButton.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedToggle from '../ThemedToggle.vue';
import ThemedDatePicker from '../form/ThemedDatePicker.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'SortFilterDropdown',
  
  components: {
    ThemedButton,
    ThemedSelect,
    ThemedToggle,
    ThemedDatePicker,
    ThemeIcon
  },
  
  props: {
    sortOptions: {
      type: Array,
      default: () => []
      // Format: [{ label, value }]
    },
    sortBy: {
      type: String,
      default: ''
    },
    sortDirection: {
      type: String,
      default: 'asc',
      validator: (v) => ['asc', 'desc'].includes(v)
    },
    filterOptions: {
      type: Array,
      default: () => []
      // Format: 
      // Toggle: [{ type: 'toggle', label, value, model }]
      // Select: [{ type: 'select', label, value, options: [{ label, value }], placeholder, model }]
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      isOpen: false
    };
  },
  
  computed: {
    activeFiltersCount() {
      let count = 0;
      
      // Count active sort (when user has selected one)
      if (this.sortBy) {
        count++;
      }
      // Count active filters
      if (this.filterOptions && this.filterOptions.length > 0) {
        count += this.filterOptions.filter(filter => {
          if (filter.type === 'toggle') {
            return filter.value === true;
          }
          if (filter.type === 'select') {
            // Count as active if a value is selected (not empty/null)
            return filter.value && filter.value !== '';
          }
          return false;
        }).length;
      }
      
      return count;
    }
  },
  
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },
    
    handleSortChange(value) {
      this.$emit('sort-change', value);
    },
    
    toggleSortDirection() {
      const newDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      this.$emit('sort-direction-change', newDirection);
    },
    
    handleFilterChange(filter, value) {
      this.$emit('filter-change', { filter: filter.model, value });
    },
    
    handleClearFilters() {
      this.$emit('clear-filters');
      this.isOpen = false;
    }
  }
};
</script>
